import React, {
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    memo,
} from 'react';
import { withCookies } from 'react-cookie';

import BidAmounts from 'components/auctions/AuctionVehicleCard/Footer/BidsForm/BidAmounts';
import AutoBidForm from 'components/auctions/AuctionVehicleCard/Footer/BidsForm/AutoBidForm';
import ModalSameBid from 'components/auctions/ModalSameBid';
import ModalDirectPurchase from 'components/auctions/ModalDirectPurchase';

import { checkValidBidFormat } from 'js/components/shared/vehicleFunctions';
import { getWSService } from 'js/WebSocketService';
import { throttle, disableHTMLOverflow } from 'Functions';
import { UserContext } from 'contexts/UserContext';
import VehicleService from 'services/VehicleService';
import Traductor from 'components/Traductor';
import Globals from '../../../../../Globals';

import 'css/components/shared/VehicleBuyOptions.css';

const VehicleBidModality = ({
    history,
    cookies,
    vehicle,
    setLoadingParent,
    disableButtons,
    options,
    updateVehiclesList,
    disabled,
    loading,
}) => {
    const t = useMemo(() => new Traductor(), []);
    const { user } = useContext(UserContext);
    const vehicleService = useMemo(() => new VehicleService(), []);
    const [autoBidInputAmount, setAutoBidInputAmount] = useState(''); //Input for max bid / auto bid
    const [isSameBid, setIsSameBid] = useState({});
    const [bidGreaterEqual, setBidGreaterEqual] = useState(); //Modal that shows if user bid is the same or equal to current vehicle bid

    const updateVehicle = useCallback(
        (
            newAmount,
            newAmountMax,
            IsActive,
            ExtendTime,
            LimitExtendedTimes,
            CounterExtendedTimes,
            AuctionStarted
        ) => {
            /*
                                    If no AmountBidMax -> User didn't bid for the vehicle -> vehicle.maxBidAWStatus = 0
                                    If no AmountBidMax  and isActive ->  User bid and winning the auction -> vehicle.maxBidAWStatus = 1
                                    If no AmountBidMax  and no isActive -> User bid but losing the auction -> vehicle.maxBidAWStatus = 2
            */
            updateVehiclesList({
                ...vehicle,
                updatedAWSAmounts: true,
                AmountAWS: newAmount,
                UserAmountBidMaxAWS: newAmountMax,
                maxBidAWStatus: !newAmountMax ? 0 : IsActive ? 1 : 2,
                AuctionStarted: AuctionStarted,
                IsActive: IsActive,
                PublicationEndDateVehicle:
                    ExtendTime || vehicle?.PublicationEndDate,
                PublicationCurrentExtension: CounterExtendedTimes
                    ? CounterExtendedTimes
                    : 0,
                PublicationMaxExtension: LimitExtendedTimes
                    ? LimitExtendedTimes
                    : 0,
            });
            setLoadingParent(false);
        },
        [setLoadingParent, updateVehiclesList, vehicle]
    );

    const getCurrentBidById = useCallback(
        async (url, apikeyname, apikeyvalue, idvehicle) => {
            const userLogged = cookies.get(process.env.REACT_APP_USER_ID);
            const token = cookies.get(Globals.COOKIES.JWT)?.AccessToken;
            if (userLogged && token) {
                try {
                    const response = await vehicleService.getBidActive(
                        `${url}?user=${encodeURIComponent(
                            userLogged.toUpperCase()
                        )}`,
                        apikeyname,
                        apikeyvalue,
                        idvehicle,
                        token
                    );
                    if (response && Object.keys(response).length > 0) {
                        if (
                            response.Dupe &&
                            Object.keys(isSameBid).length > 0 &&
                            !isSameBid.active
                        ) {
                            setIsSameBid(state => ({
                                ...state,
                                active: true,
                            }));
                        }
                        if (response.Amount !== '') {
                            updateVehicle(
                                response.Amount,
                                response.AmountMax,
                                response.IsActive,
                                response.ExtendTime,
                                response.LimitExtendedTimes,
                                response.CounterExtendedTimes,
                                true
                            );
                        }
                    } else {
                        //Response is {} from No bids or Anular pujas -> Set car bid status to initial, needed for bids cancel feature
                        updateVehicle('', '', true, null, null, null, true);
                    }
                } catch (error) {}
                disableButtons(false);
            }
        },
        [cookies, disableButtons, isSameBid, updateVehicle, vehicleService]
    );

    useEffect(() => {
        if (vehicle?.HasBidModality && !vehicle.updatedAWSAmounts) {
            setTimeout(() => {
                getCurrentBidById(
                    process.env.REACT_APP_AWS_GET_BIDS_URL,
                    process.env.REACT_APP_AWS_BIDS_APIKEY_NAME,
                    process.env.REACT_APP_AWS_BIDS_APIKEY_VALUE,
                    vehicle.WebSalesId
                );
            }, 150);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle.updatedAWSAmounts]); //FETCH BIDS INITIALLY AND WHENEVER SOCKET MESSAGE ARRIVES

    useEffect(() => {
        if (!isSameBid || (isSameBid && !isSameBid.active))
            disableHTMLOverflow(false);
    }, [isSameBid]);

    const getCostumerId = useCallback(() => {
        if (user.buyerType === 1 && user.cifs.length === 1) {
            return user.cifs[0].Id.toString();
        }

        return user.preferredLocation && user.preferredLocation.ClientId
            ? user.preferredLocation.ClientId.toString()
            : null;
    }, [user.buyerType, user.cifs, user.preferredLocation]);

    const sendMessage = useCallback(bid => {
        const message = {
            type: 'pujas',
            data: {
                Vehicles: [+bid.IdVehicle],
            },
        };
        getWSService().sendMessage('onMessage', message);
    }, []);

    const setNewAmounts = useCallback(
        (r, bid, type) => {
            if (type === 'input' && autoBidInputAmount)
                setAutoBidInputAmount('');

            const actualAmount =
                vehicle.AmountAWS === 0
                    ? vehicle.MinimumPrice
                    : vehicle.AmountAWS + vehicle.DifferentialAmount;
            setIsSameBid({
                active: false,
                maxBidInput:
                    type === 'input' ? autoBidInputAmount : actualAmount,
                differentialAmount: vehicle.DifferentialAmount,
                sameAmount:
                    type === 'input' ? autoBidInputAmount : actualAmount,
            });

            sendMessage(bid);
        },
        [
            autoBidInputAmount,
            sendMessage,
            vehicle.AmountAWS,
            vehicle.DifferentialAmount,
            vehicle.MinimumPrice,
        ]
    );

    const validateNewBid = useCallback(
        bid => {
            if (!checkValidBidFormat(bid)) {
                alert(t.getTraduction('InvalidBidFormat'));
                return false;
            } else {
                if (
                    // (vehicle.Price && bid >= vehicle.Price) ||
                    // bid <= vehicle.AmountAWS ||
                    bid === vehicle.UserAmountBidMaxAWS
                ) {
                    return false;
                }
                if (
                    bid <= vehicle.AmountAWS ||
                    (vehicle.AmountAWS === 0 &&
                        vehicle.MinimumPrice > 0 &&
                        bid < vehicle.MinimumPrice)
                ) {
                    alert(t.getTraduction('InvalidBidAmount'));
                    return false;
                } else if (vehicle.Price && bid >= vehicle.Price) {
                    setBidGreaterEqual(bid);
                    disableHTMLOverflow(true);
                    return false;
                }
            }
            return true;
        },
        [
            t,
            vehicle.AmountAWS,
            vehicle.MinimumPrice,
            vehicle.Price,
            vehicle.UserAmountBidMaxAWS,
        ]
    );

    const onBid = useCallback(
        throttle(async (inputType, newMaxAmount) => {
            const newBidAmount = !vehicle.AmountAWS
                ? vehicle.MinimumPrice
                : parseInt(vehicle.AmountAWS) +
                  parseInt(vehicle.DifferentialAmount);
            const bid = {
                IdVehicle: vehicle.WebSalesId.toString(),
                Bid: newBidAmount.toString(),
                BidMax: newBidAmount.toString(),
                IdPreferenceTransport: user?.preferredLocation?.PreferenceId
                    ? user.preferredLocation.PreferenceId.toString()
                    : null,
                IdUser: cookies.get(Globals.COOKIES.USER),
                IdCustomer: getCostumerId(),
                BatchDetail: 'null',
            };
            switch (inputType) {
                case 'button':
                    if (!validateNewBid(newBidAmount)) return;
                    break;
                case 'input':
                    if (
                        !autoBidInputAmount ||
                        !validateNewBid(autoBidInputAmount)
                    )
                        return;
                    bid.BidMax = autoBidInputAmount.toString();
                    break;
                case 'modal':
                    if (!newMaxAmount || !validateNewBid(newMaxAmount)) return;
                    bid.BidMax = newMaxAmount.toString();
                    break;
                default:
            }
            disableButtons(true);
            const r = await vehicleService.insertBid(
                process.env.REACT_APP_AWS_BIDS_URL,
                process.env.REACT_APP_AWS_BIDS_APIKEY_NAME,
                process.env.REACT_APP_AWS_BIDS_APIKEY_VALUE,
                bid,
                cookies.get(Globals.COOKIES.JWT).AccessToken
            );
            setNewAmounts(r, bid, inputType);
            return r === 'OK' || r === 'AUTO';
        }, 200),
        [
            autoBidInputAmount,
            cookies,
            disableButtons,
            getCostumerId,
            setNewAmounts,
            user.preferredLocation.PreferenceId,
            validateNewBid,
            vehicle.DifferentialAmount,
            vehicle.AmountAWS,
            vehicle.WebSalesId,
            vehicle.MinimumPrice,
            vehicleService,
        ]
    );

    const printModalSameBid = useCallback(() => {
        if (isSameBid && isSameBid.active && vehicle.Status === 2) {
            return (
                <ModalSameBid
                    options={isSameBid}
                    closeModal={setIsSameBid}
                    validateNewBid={newMaxBidAmount =>
                        onBid('modal', newMaxBidAmount)
                    }
                />
            );
        }
    }, [isSameBid, onBid, vehicle.Status]);

    const closeBidGreaterEqual = useCallback(() => {
        setBidGreaterEqual(null);
        disableHTMLOverflow(false);
    }, []);

    const printModalDirectPurchase = useCallback(() => {
        if (!bidGreaterEqual || vehicle.Status !== 2) return null;
        const optionsModal = {
            vehicle: vehicle,
            bid: bidGreaterEqual,
        };
        return (
            <ModalDirectPurchase
                history={history}
                options={optionsModal}
                closeModal={closeBidGreaterEqual}
            />
        );
    }, [bidGreaterEqual, closeBidGreaterEqual, history, vehicle]);

    const marginTopNone = options.type === 'detailvehicle' ? 'u-mt-none' : '';
    const paddingNone = options.type === 'vehiclecard' ? 'u-p-none' : '';
    return (
        <>
            <hr />
            <BidAmounts
                vehicle={vehicle}
                disabled={disabled}
                loading={loading}
                onBid={onBid}
                padding={paddingNone}
            />
            <AutoBidForm
                marginTop={marginTopNone}
                onBid={onBid}
                disabled={disabled}
                loading={loading}
                vehicle={vehicle}
                type={options.type}
                inputAmount={autoBidInputAmount}
                onInputAmountChange={setAutoBidInputAmount}
            />
            {printModalSameBid()}
            {printModalDirectPurchase()}
        </>
    );
};

export default memo(withCookies(VehicleBidModality));
